import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { selectAccountId } from '../../../../../../store/auth/authSlice'

import { useLazyGetFeaturesQuery } from '../../../../../../store/ai/ai'
import { useLazyGetAllScorecardsQuery, useLazyGetScorecardResultsByFilterQuery } from '../../../../../../store/ai/scorecard'
import { useLazyListRoomEventsQuery } from '../../../../../../store/media/media'
import {
  selectClientProfileAiFeatures,
  selectClientProfileEventsForPolling,
  selectClientProfileResults,
  setClientProfileAiFeatures,
  setClientProfileAiFeaturesLoading,
  setClientProfileEventsForPolling,
  setClientProfileEventsForPollingLoading,
  setClientProfileResults,
  setClientProfileScorecards,
  setClientProfileScorecardsLoading,
  resetClientProfileMeetingData,
} from '../../../../../../store/meetingSummary/clientProfile'

export const ClientProfileInitialization = () => {
  const { roomId = '', sessionId = '' } = useParams()
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      dispatch(resetClientProfileMeetingData())
    }
  }, [dispatch, roomId, sessionId])

  useInitializeClientProfile(accountId, roomId, sessionId)
  return null
}

export const ScorecardInitialization = () => {
  const accountId = useAppSelector(selectAccountId)
  useInitializeClientProfileAiFeatures(accountId, '', '')
  useInitializeClientProfileScorecards(accountId)
  return null
}

const useInitializeClientProfile = (accountId: string, roomId: string, sessionId: string) => {
  useInitializeClientProfileAiFeatures(accountId, roomId, sessionId)
  useInitializeClientProfileEventsForPolling(accountId, roomId, sessionId)
  useInitializeClientProfileScorecards(accountId)
  useInitializeClientProfileResults(accountId, roomId, sessionId)
}

const useInitializeClientProfileAiFeatures = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const clientProfileAiFeatures = useAppSelector(selectClientProfileAiFeatures)

  const [triggerGetAiFeatures] = useLazyGetFeaturesQuery()

  useEffect(() => {
    const fetchClientProfileAiFeatures = async () => {
      if (clientProfileAiFeatures.length) {
        return
      }
      dispatch(setClientProfileAiFeaturesLoading(true))
      try {
        const { data: clientProfileAiFeatures } = await triggerGetAiFeatures(
          {
            accountId,
            pageRequest: { page: 0, size: 500 },
            includeRestricted: true,
            type: 'scorecard',
          },
          false,
        ).unwrap()
        dispatch(setClientProfileAiFeatures(clientProfileAiFeatures))
      } finally {
        dispatch(setClientProfileAiFeaturesLoading(false))
      }
    }
    fetchClientProfileAiFeatures()
  }, [dispatch, accountId, roomId, sessionId, triggerGetAiFeatures, clientProfileAiFeatures.length])
}

const useInitializeClientProfileEventsForPolling = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const eventsForPolling = useAppSelector(selectClientProfileEventsForPolling)

  const [triggerGetRoomEvents] = useLazyListRoomEventsQuery()

  useEffect(() => {
    if (eventsForPolling.length) {
      return
    }
    // Abort and ignore the response on meeting switch so a previous meeting's in-flight request can't overwrite the new one.
    let aborted = false
    dispatch(setClientProfileEventsForPollingLoading(true))
    const promise = triggerGetRoomEvents(
      {
        accountId,
        roomId,
        sessionId,
        pageRequest: { page: 0, size: 500 },
        types: ['aiFeatureExecutionFinished', 'userAllocationUpdated'],
      },
      true,
    )
    promise
      .unwrap()
      .then((roomEvents) => {
        if (aborted) {
          return
        }
        if (roomEvents?.data?.length) {
          dispatch(setClientProfileEventsForPolling(roomEvents.data))
        }
        dispatch(setClientProfileEventsForPollingLoading(false))
      })
      .catch((e: unknown) => {
        if (aborted) {
          return
        }
        console.error(e)
        dispatch(setClientProfileEventsForPollingLoading(false))
      })
    return () => {
      aborted = true
      promise.abort()
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetRoomEvents, eventsForPolling.length])
}

const useInitializeClientProfileScorecards = (accountId: string) => {
  const dispatch = useAppDispatch()
  const clientProfileAiFeatures = useAppSelector(selectClientProfileAiFeatures)
  const [triggerGetScorecards] = useLazyGetAllScorecardsQuery()

  useEffect(() => {
    if (!clientProfileAiFeatures.length) {
      return
    }
    const fetchClientProfileScoreCards = async () => {
      const aiFeatureIds = clientProfileAiFeatures.map((aiFeature) => aiFeature.id)
      dispatch(setClientProfileScorecardsLoading(true))

      try {
        const { data: scorecardsData } = await triggerGetScorecards(
          {
            accountId,
            aiFeatureIds: aiFeatureIds.join(','),
          },
          false,
        ).unwrap()
        dispatch(setClientProfileScorecards(scorecardsData))
      } catch (e: unknown) {
        console.error(e)
      } finally {
        dispatch(setClientProfileScorecardsLoading(false))
      }
    }
    fetchClientProfileScoreCards()
  }, [dispatch, accountId, clientProfileAiFeatures, triggerGetScorecards])
}

const useInitializeClientProfileResults = (accountId: string, roomId: string, sessionId: string) => {
  const dispatch = useAppDispatch()

  const clientProfileResults = useAppSelector(selectClientProfileResults)

  const [triggerGetScorecardResults] = useLazyGetScorecardResultsByFilterQuery()

  useEffect(() => {
    if (clientProfileResults.length) {
      return
    }
    // Abort and ignore the response on meeting switch so a previous meeting's in-flight request can't overwrite the new one.
    let aborted = false
    const promise = triggerGetScorecardResults(
      {
        accountId,
        body: {
          source: {
            type: 'meeting',
            roomId: roomId,
            sessionId: sessionId,
          },
        },
      },
      false,
    )
    promise
      .unwrap()
      .then((results) => {
        if (aborted) {
          return
        }
        dispatch(setClientProfileResults(results))
      })
      .catch((e: unknown) => {
        if (aborted) {
          return
        }
        console.error(e)
      })
    return () => {
      aborted = true
      promise.abort()
    }
  }, [dispatch, accountId, roomId, sessionId, triggerGetScorecardResults, clientProfileResults.length])
}
